import React, { useState, useEffect } from 'react';
import { Button, Table, message as messageApi } from 'antd';
import type { TableColumnsType } from 'antd';
import FilterArea, { DEFAULT_CHANNEL_FILTER_OPTIONS, type ChannelFilterOptions } from './components/FilterArea';
import ChannelFormDrawer from './components/ChannelFormDrawer';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { GET_ALL_CHANNELS, useAllChannels } from './graphql/queries/getAllChannels';
import { useSaveChannel } from './graphql/mutations/saveChannel';
import { showError } from 'utils/exceptionUtils';
import { ChannelType } from './graphql/types/ChannelType';
import Loading from "../../../components/Loading";
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

const PageBoy: React.FC = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState<ChannelType | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filteredData, setFilteredData] = useState<ChannelType[]>([]);
    const [filters, setFilters] = useState<ChannelFilterOptions>(DEFAULT_CHANNEL_FILTER_OPTIONS);
    const {t} = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // CRM 资源码：入口在 CentralLayout 已校验 CRM_CHANNEL，这里控制页内按钮与详情只读
    const canCreateChannel = hasResource([ResourceCode.crm.CRM_CHANNEL_CREATE]);
    const canFilterChannel = hasResource([ResourceCode.crm.CRM_CHANNEL_FILTER]);
    const canEditChannel = hasResource([ResourceCode.crm.CRM_CHANNEL_EDIT]);
    // GraphQL hooks
    const { allChannels, loading, error, refetch } = useAllChannels();
    const [saveChannel, { loading: savingChannel }] = useSaveChannel();

    // Initialize filtered data when channelData changes
    useEffect(() => {
        setFilteredData(allChannels);
        setFilters(DEFAULT_CHANNEL_FILTER_OPTIONS);
    }, [allChannels]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    // Handle GraphQL errors
    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error]);

    const handleFilter = () => {
        let result = [...allChannels];

        if (filters.channelId) {
            const id = filters.channelId.toLowerCase();
            result = result.filter(channel => channel.id?.toLowerCase().includes(id));
        }

        if (filters.channelName) {
            const name = filters.channelName.toLowerCase();
            result = result.filter(channel => channel.channelName.toLowerCase().includes(name));
        }

        if (filters.status !== 'all') {
            const statusValue = filters.status === 'In Business' ? 1 : 0;
            result = result.filter(channel => channel.status === statusValue);
        }

        setFilteredData(result);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters(DEFAULT_CHANNEL_FILTER_OPTIONS);
        setFilteredData(allChannels);
        setCurrentPage(1);
    };

    // Show new Channel drawer
    const showNewChannelDrawer = () => {
        setSelectedChannel(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    // Show Channel details
    const showChannelDetail = (channel: ChannelType) => {
        setSelectedChannel(channel);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle Channel form submission (create or update)
    const handleChannelSubmit = async (values: ChannelType) => {
        try {
            const channelInput = {
                id: values.id || undefined,
                channelName: values.channelName,
                companyName: values.companyName,
                companyAddress: values.companyAddress || undefined,
                contactName: values.contactName || undefined,
                contactPhone: values.contactPhone || undefined,
                email: values.email || undefined,
                taxRate: values.taxRate || undefined,
                taxId: values.taxId || undefined,
                isAutoGenerate: values.isAutoGenerate,
                isSetSettlementConfig: values.isSetSettlementConfig || false,
                settlementCustomerType: values.settlementCustomerType,
                settlementTrackingPoint: values.settlementTrackingPoint,
                billingCycleConfiguration: values.billingCycleConfiguration,
                weightCalculationMode: values.weightCalculationMode,
                weightCalculationThresholdX: values.weightCalculationThresholdX !== undefined && values.weightCalculationThresholdX !== null 
                    ? Number(values.weightCalculationThresholdX) 
                    : undefined,
                settlementMode: values.settlementMode,
                billType: Array.isArray(values.billType) ? values.billType : (values.billType ? [values.billType] : undefined),
                billUnitType: values.billUnitType,
                timeZoneIana: values.timeZoneIana,
                schemeLimitNode: values.schemeLimitNode || undefined,
                status: values.status,
                remark: values.remark || undefined
            };

            await saveChannel({
                variables: { input: channelInput },
                refetchQueries: [{ query: GET_ALL_CHANNELS }]
            });

            if (drawerMode === 'create') {
                messageApi.success(t('central.channels.messages.channelCreatedSuccessfully') as string);
            } else {
                messageApi.success(t('central.channels.messages.channelUpdatedSuccessfully') as string);
            }

            setDrawerVisible(false);
            // 重新获取数据
            refetch();
        } catch (error: any) {
            showError(error);
        }
    };

    // Table column definitions
    const channelColumns: TableColumnsType<ChannelType> = [
        {
            title: t('central.channels.columns.id'),
            dataIndex: 'id',
            key: 'id',
            align: 'left',
            render: (text, record) => (
                <Button
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => showChannelDetail(record)}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: t('central.channels.columns.name'),
            dataIndex: 'channelName',
            key: 'channelName',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.channels.columns.autoGenerateAppointment'),
            dataIndex: 'isAutoGenerate',
            key: 'isAutoGenerate',
            align: 'left' as const,
            ellipsis: false,
            render: (isAutoGenerate: boolean) => {
                const text = isAutoGenerate ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.channels.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const text = status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('central.channels.columns.company'),
            dataIndex: 'companyName',
            key: 'companyName',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('central.channels.columns.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        }
    ];

    if (loading) {
        return (
            <Loading spinning={loading} />
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            {canFilterChannel && (
                <FilterArea
                    filters={filters}
                    setFilters={setFilters}
                />
            )}

            {/* 按钮区域 - 新建按钮和筛选/重置按钮同一行 */}
            <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                {canCreateChannel && (
                    <Button
                        type="primary"
                        onClick={showNewChannelDrawer}
                        loading={savingChannel}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.new')}</span>
                    </Button>
                )}
                {canFilterChannel && (
                    <>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.reset')}</span>
                        </Button>
                    </>
                )}
            </div>

            {/* Channel table */}
            <Table
                rowKey="id"
                columns={channelColumns}
                dataSource={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                loading={loading}
                pagination={false}
                scroll={{x: 'max-content'}}
                size="middle"
                tableLayout="auto"
                className="mb-4"
            />

            <TablePaginationBar
                total={filteredData.length}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) setPageSize(size);
                }}
                onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* Channel form drawer (shared for create/edit) */}
            <ChannelFormDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleChannelSubmit}
                channel={selectedChannel}
                mode={drawerMode}
                readOnly={drawerMode === 'edit' && !canEditChannel}
            />
        </div>
    );
};

const ChannelsPage: React.FC = () => {
    return (
        <PageBoy />
    );
};

export default ChannelsPage;